// Succession & promotion pool (WF-16) — the tenant-wide list of evaluated people
// flagged promotion-ready or with a compensation impact (pay outside their graded
// band). From here HR can propose the locked grade as the employee's career_level
// through employee-service's maker-checker — an explicit, comp-gated action that
// NEVER writes pay directly; it enters an approval queue.
//
// docs/workforce-framework/integrations.md (recommend, don't move money) + WF-16/17.
import React, { useEffect, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { ArrowUpCircle, Loader2, ShieldCheck, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { useHasPermission } from '@/rbac/useRbac';
import {
  applyGrade, getSuccession, isNotRolledOut,
  type SuccessionRow,
} from '@/services/workforceService';

const errMsg = (e: any, fallback: string) =>
  e?.response?.data?.detail || e?.response?.data?.message || e?.message || fallback;

export const SuccessionTab: React.FC = () => {
  const { t } = useTranslation('workforce');
  const canComp = useHasPermission('workforce:comp_impact');
  const confirm = useConfirm();

  const [rows, setRows] = useState<SuccessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState<string | null>(null);
  const [notRolledOut, setNotRolledOut] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await getSuccession());
    } catch (e) {
      if (isNotRolledOut(e)) setNotRolledOut(true);
      else toast.error(getApiErrorMessage(e, errMsg(e, 'Could not load the succession pool')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onApply = async (row: SuccessionRow) => {
    const ok = await confirm({
      title: t('succ_apply_title', { defaultValue: 'Propose grade change?' }),
      message: t('succ_apply_body', {
        defaultValue:
          'This sends the grade to the employee record as a proposed change. It enters the HR approval queue — it does not change pay or apply immediately.',
      }),
      confirmLabel: t('succ_apply_confirm', { defaultValue: 'Propose change' }),
    });
    if (!ok) return;
    setApplying(row.subject_id);
    try {
      const p = await applyGrade(row.subject_id);
      if (p.status === 'applied') {
        toast.success(t('succ_applied', { defaultValue: 'Grade applied to the employee record.' }));
      } else {
        toast.success(
          t('succ_queued', {
            defaultValue: 'Proposed — awaiting HR approval (change request created).',
          }),
        );
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, errMsg(e, 'Could not propose the grade change')));
    } finally {
      setApplying(null);
    }
  };

  if (notRolledOut) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          {t('succ_not_ready', {
            defaultValue: 'The workforce engine is not available in this workspace yet.',
          })}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <TrendingUp className="w-4 h-4" />
        {t('succ_intro', {
          defaultValue:
            'People flagged promotion-ready or with pay outside their evaluated band. Proposing a grade routes through HR approval — it never changes pay.',
        })}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              {t('succ_empty', {
                defaultValue: 'No promotion-ready or comp-impacted results yet.',
              })}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('succ_person', { defaultValue: 'Person' })}</TableHead>
                  <TableHead>{t('succ_grade', { defaultValue: 'Grade' })}</TableHead>
                  <TableHead>{t('succ_flags', { defaultValue: 'Flags' })}</TableHead>
                  <TableHead>{t('succ_status', { defaultValue: 'Status' })}</TableHead>
                  {canComp && <TableHead className="text-right" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.subject_id}>
                    <TableCell>
                      <div className="font-medium">{r.label || r.subject_ref}</div>
                      {r.label && (
                        <div className="text-xs text-muted-foreground">{r.subject_ref}</div>
                      )}
                    </TableCell>
                    <TableCell>{r.grade_code || '—'}</TableCell>
                    <TableCell className="space-x-1">
                      {r.promotion_ready && (
                        <Badge variant="secondary">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          {t('succ_ready', { defaultValue: 'Promotion ready' })}
                        </Badge>
                      )}
                      {r.comp_impact && (
                        <Badge variant="destructive">
                          {t('succ_comp', { defaultValue: 'Comp impact' })}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{r.status}</Badge>
                    </TableCell>
                    {canComp && (
                      <TableCell className="text-right">
                        {r.status === 'locked' && r.grade_code && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={applying === r.subject_id}
                            onClick={() => onApply(r)}
                          >
                            {applying === r.subject_id ? (
                              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                            ) : (
                              <ArrowUpCircle className="w-4 h-4 mr-1.5" />
                            )}
                            {t('succ_apply', { defaultValue: 'Propose grade' })}
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {canComp && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="w-3.5 h-3.5" />
          {t('succ_gate_note', {
            defaultValue:
              'Grade proposals require the compensation-impact permission and are recorded in the audit log.',
          })}
        </div>
      )}
    </div>
  );
};

export default SuccessionTab;
